import {AuthProfile, Wish} from "@/api/types.ts";
import {Avatar, Badge, Blockquote, Button, Image, Spinner, Caption} from "@telegram-apps/telegram-ui";
import {TbGift, TbWorldShare} from "react-icons/tb";
import {BookButton} from "@/components/Wish/BookButton.tsx";
import {openExternalLink} from "@/helpers/openExternalLink.ts";
import {useTranslation} from "react-i18next";
import {useState} from "react";
import {Link} from "@/components/Link/Link.tsx";
import {useAuthProfile} from "@/providers/AuthProfileProvider.tsx";
import {haptic} from "@/helpers/haptic.ts";
import {getUserAcronym} from "@/helpers/getUserAcronym.ts";
import './styles.css';

export const WishView = (
  { wish, onUpdate, refreshWishlist, onCloseWish }:
  { wish: Wish | null, onUpdate: (wish: Wish) => void, refreshWishlist?: () => Promise<void>, onCloseWish?: () => void }
) => {
  const { t } = useTranslation();
  const [isImageLoaded, setIsImageLoaded] = useState<boolean>(false);

  let authProfileFromContext: AuthProfile | undefined = undefined;
  try {
    const { authProfile } = useAuthProfile();
    authProfileFromContext = authProfile
  } catch (e) { /* empty */ }

  if (!wish) {
    return (<>Wish not loaded</>);
  }
  const maxSizeImage = wish.image?.sizes[wish.image?.sizes.length - 1];
  let vertical = false;
  if (maxSizeImage && maxSizeImage.height >= maxSizeImage.width) {
    vertical = true
  }

  const openWishLink = () => {
    if (wish.displayUrl) {
      haptic.impact('light');
      openExternalLink(wish.displayUrl);
    } else if (wish.url) {
      haptic.impact('light');
      openExternalLink(wish.url);
    }
  }

  const renderDescription = (description: string | null | undefined) => {
    if (!description) {
      return null;
    }

    const parts = description.split(/(https?:\/\/[^\s]+)/)
    for (let i = 1; i < parts.length; i += 2) {
      // @ts-expect-error it works
      parts[i] = <Link key={'link' + i} to={parts[i]}>{parts[i]}</Link>
    }
    return parts;
  }

  return (
    <div className={"flex flex-col content-center justify-center items-center gap-3 ml-5 mr-5 " + (!wish.description ? "pb-10" : "")}>
      <div className={"relative"}>
        {maxSizeImage && <div className={"p-3 min-w-20 max-h-56 overflow-hidden flex justify-center items-center relative"}
                              style={{height: maxSizeImage?.height + "px"}}>
          {!isImageLoaded && <Spinner className={"image-loader"} size={"l"}/>}
          <img height={vertical ? maxSizeImage.height : undefined}
               width={!vertical ? maxSizeImage.width : undefined}
               className={"object-cover max-h-60 shiny-image max-w-full rounded-lg " + (isImageLoaded ? "visible" : "invisible")}
               src={maxSizeImage.link}
               onLoad={() => setIsImageLoaded(true)}
               alt={wish.title}/>
        </div>}

        {!maxSizeImage && <div className={"p-3"}>
          <Image className={"shiny-image"}
                 fallbackIcon={<TbGift color={"#fff"} size={120}/>}
                 style={{
                   background: wish.randomGradient,
                 }}
            // @ts-expect-error size may have this value
                 size={200}/>
        </div>}

        {wish.priceText && <Badge className={"absolute top-3 right-3 "} large={true}
               type={"number"} mode={"critical"}>
          {wish.priceText}
        </Badge>}
      </div>

      {wish.url &&
        <Button before={<TbWorldShare size={25} />}
                onClick={openWishLink}
                stretched={true}
                mode={"bezeled"}>
          {t('wish.action.openLink')}
      </Button>}

      {wish.bookedBy === "currentUser" && (
        <div className="flex justify-center">
          <Caption weight="2" className="text-gray-600">
            {t('wish.bookedByYou')}
          </Caption>
        </div>
      )}

      {
        wish.bookedBy === "otherUser" &&
        authProfileFromContext?.user.showBookings === "public" && wish.bookedByUser && (
        <div className="flex items-center gap-2 mt-1">
          <Caption weight="2" className="text-gray-600">
            {t('wish.bookedBy')}
          </Caption>
          <Link to={`/profiles/${wish.bookedByUser.id}`} className="flex items-center gap-2 no-underline">
            <Avatar 
              src={wish.bookedByUser.photo?.sizes[0]?.link}
              acronym={getUserAcronym(wish.bookedByUser.fullname)}
              size={24}
            />
            <Caption weight="2">
              {wish.bookedByUser.fullname}
            </Caption>
          </Link>
        </div>
      )}


      <BookButton wish={wish} onUpdate={onUpdate} refreshWishlist={refreshWishlist} onCloseWish={onCloseWish}/>

      {wish.description && <Blockquote type="text" className={"description"}>
        {renderDescription(wish.description)}
      </Blockquote>}
    </div>
  );
}
